<script>
export default {
  props: {
    secondaryItem: {
      type: Object,
      required: true,
    },
    children: {
      type: Array,
      default: () => [],
    },
    showAll: {
      type: Boolean,
      default: false,
    },
    canShowNewButton: {
      type: Boolean,
      default: false,
    },
    enableSearch: {
      type: Boolean,
      default: false,
    },
    showSearch: {
      type: Boolean,
      default: false,
    },
    searchQuery: {
      type: String,
      default: '',
    },
    filteredChildren: {
      type: Array,
      default: null,
    },
  },
  computed: {
    visibleChildren() {
      if (this.enableSearch && this.searchQuery) {
        return this.filteredChildren || [];
      }
      return this.children;
    },
    firstThree() {
      if (this.enableSearch && this.searchQuery) {
        return this.visibleChildren;
      }
      return this.children.slice(0, 3);
    },
    remaining() {
      if (this.enableSearch && this.searchQuery) {
        return [];
      }
      return this.children.slice(3);
    },
    hasMore() {
      if (this.enableSearch && this.searchQuery) {
        return false;
      }
      return this.children.length > 3;
    },
    showEmptyState() {
      return (
        this.enableSearch &&
        this.searchQuery &&
        this.visibleChildren.length === 0
      );
    },
  },
  methods: {
    onNewClick() {
      this.$emit('new-click', this.secondaryItem);
    },
    onToggleSearch() {
      this.$emit('toggle-search');
    },
    onToggleShowAll() {
      this.$emit('toggle-show-all');
    },
  },
};
</script>

<template>
  <div class="mt-2">
    <!-- Header: title + actions -->
    <div
      class="flex items-center justify-between pr-2"
      :class="{ 'mb-1': children.length }"
    >
      <h4
        class="px-4 pt-2 pb-1 text-xs font-medium tracking-wider text-primary-300"
      >
        {{ $t(`SIDEBAR.${secondaryItem.label}`) }}
      </h4>
      <div v-if="enableSearch" class="flex items-center">
        <woot-button
          size="tiny"
          variant="clear"
          color-scheme="white"
          icon="search"
          @click.stop="onToggleSearch"
        />
        <woot-button
          v-if="canShowNewButton"
          size="tiny"
          variant="clear"
          color-scheme="white"
          icon="add"
          @click.stop="onNewClick"
        />
      </div>
      <woot-button
        v-else-if="canShowNewButton"
        size="tiny"
        variant="clear"
        color-scheme="white"
        icon="add"
        @click.stop="onNewClick"
      />
    </div>

    <!-- Search input (labels only) -->
    <div v-if="enableSearch && showSearch" class="px-4 mb-1">
      <div
        class="flex items-center bg-primary-900/40 border border-primary-900/40 rounded-md px-2 focus-within:bg-primary-900/60 focus-within:border-primary-900/60 transition-all"
      >
        <fluent-icon
          icon="search"
          size="14"
          class="text-primary-300 flex-shrink-0"
        />
        <input
          :value="searchQuery"
          type="text"
          class="flex-1 h-8 ml-1 text-xs !mb-0 !bg-transparent !border-none !shadow-none !outline-none text-white placeholder-primary-300 focus:!ring-0"
          :placeholder="$t('SEARCH_LABELS')"
          auto-focus
          @input="$emit('update:searchQuery', $event.target.value)"
        />
        <fluent-icon
          v-if="searchQuery"
          icon="dismiss"
          size="14"
          class="text-primary-300 cursor-pointer hover:text-white flex-shrink-0"
          @click.native="$emit('update:searchQuery', '')"
        />
      </div>
    </div>

    <!-- First 3 (or filtered) children -->
    <slot name="item" :items="firstThree" />

    <!-- Empty state for search -->
    <div
      v-if="showEmptyState"
      class="px-4 py-2 text-xxs text-primary-300 italic"
    >
      {{ $t('NO_LABELS_FOUND') }}
    </div>

    <!-- Remaining children when expanded -->
    <div v-if="showAll && hasMore">
      <slot name="item-remaining" :items="remaining" />
    </div>

    <!-- Show more / less button -->
    <div v-if="hasMore" class="px-4 py-2">
      <woot-button
        size="small"
        variant="clear"
        color-scheme="white"
        :icon="showAll ? 'chevron-up' : 'chevron-down'"
        @click="onToggleShowAll"
      >
        {{
          showAll
            ? $t('COMPONENTS.SHOW_MORE_BLOCK.SHOW_LESS')
            : $t('COMPONENTS.SHOW_MORE_BLOCK.SHOW_MORE')
        }}
      </woot-button>
    </div>
  </div>
</template>
